"use client";

import React from "react";
import Link from "next/link";
import type { HeroLayer } from "@/themes/default/templates/section/types";
import { getLocalizedText } from "@/themes/default/templates/section/utils";

/**
 * Shared positioned-content-layers overlay — the legacy Revolution-Slider
 * `tp-caption` behaviour, reused by BOTH the layered hero and the carousel.
 *
 * Renders an array of `HeroLayer` absolutely positioned over a slide: each
 * layer is anchored (9-point) with a % offset (responsive, no fixed px), and
 * animates in (fade / fade-up / … / zoom) with a per-layer delay when its
 * slide is active. Positioning lives on an OUTER wrapper and the entrance
 * animation on a keyed INNER node so their transforms never fight.
 *
 * Requires a positioned (relative/absolute) parent sized to the slide.
 */

const ANCHOR_BASE: Record<string, { x: number; y: number }> = {
  "top-left": { x: 0, y: 0 }, "top-center": { x: 50, y: 0 }, "top-right": { x: 100, y: 0 },
  "middle-left": { x: 0, y: 50 }, "middle-center": { x: 50, y: 50 }, "middle-right": { x: 100, y: 50 },
  "bottom-left": { x: 0, y: 100 }, "bottom-center": { x: 50, y: 100 }, "bottom-right": { x: 100, y: 100 },
};

function anchorTranslate(anchor: string): string {
  const b = ANCHOR_BASE[anchor] ?? ANCHOR_BASE["top-left"];
  const tx = b.x === 0 ? "0" : b.x === 50 ? "-50%" : "-100%";
  const ty = b.y === 0 ? "0" : b.y === 50 ? "-50%" : "-100%";
  return `translate(${tx}, ${ty})`;
}

/** Anchor + %-offset placement of a layer (shared with sliders that render layers themselves). */
export function layerPositionStyle(layer: HeroLayer): React.CSSProperties {
  return positionStyle(layer);
}

function positionStyle(layer: HeroLayer): React.CSSProperties {
  const anchor = layer.anchor ?? "top-left";
  const b = ANCHOR_BASE[anchor] ?? ANCHOR_BASE["top-left"];
  return {
    position: "absolute",
    left: `${b.x + (layer.xOffset ?? 0)}%`,
    top: `${b.y + (layer.yOffset ?? 0)}%`,
    transform: anchorTranslate(anchor),
    width: layer.width ? `${layer.width}%` : undefined,
    maxWidth: "92%",
    zIndex: layer.zIndex ?? 1,
  };
}

function visualStyle(layer: HeroLayer, active: boolean): React.CSSProperties {
  const anim = layer.animation && layer.animation !== "none" ? layer.animation : null;
  return {
    color: layer.color || "#FFFFFF",
    backgroundColor: layer.type === "box" ? layer.bgColor || "rgba(0,0,0,0.45)" : undefined,
    padding: layer.padding != null ? `${layer.padding}px` : layer.type === "box" ? "16px" : undefined,
    borderRadius: layer.radius != null ? `${layer.radius}px` : undefined,
    textAlign: layer.textAlign || "left",
    fontSize: layer.fontSize ? `${layer.fontSize}px` : undefined,
    fontWeight:
      layer.fontWeight === "bold" ? 700 : layer.fontWeight === "semibold" ? 600 : undefined,
    textTransform: layer.uppercase ? "uppercase" : undefined,
    ...(active && anim
      ? {
          animationName: `epona-hero-${anim}`,
          animationDuration: "700ms",
          animationTimingFunction: "cubic-bezier(0.22,1,0.36,1)",
          animationFillMode: "both",
          animationDelay: `${layer.animationDelay ?? 0}ms`,
        }
      : { opacity: active ? 1 : 0 }),
  };
}

function LayerNode({
  layer,
  active,
  currentLanguage,
  slideKey,
  sectionNode,
}: {
  layer: HeroLayer;
  active: boolean;
  currentLanguage: "en" | "mm";
  slideKey: number | string;
  /** Pre-rendered node for a type 'section' layer (resolved server-side). */
  sectionNode?: React.ReactNode;
}) {
  const text = layer.text ? getLocalizedText(layer.text, currentLanguage) : "";

  let inner: React.ReactNode = null;
  if (layer.type === "section") {
    inner = sectionNode ?? null;
  } else if (layer.type === "image") {
    inner = layer.image ? (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={layer.image} alt={text || ""} className="block w-full h-auto object-contain" />
    ) : null;
  } else if (layer.type === "heading") {
    inner = <span className="epona-heading block leading-tight">{text}</span>;
  } else {
    inner = (
      <>
        {layer.image && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={layer.image} alt="" className="block mb-2 max-h-24 w-auto object-contain" />
        )}
        {text && <span className="block leading-snug whitespace-pre-line">{text}</span>}
      </>
    );
  }

  const node = (
    <div style={positionStyle(layer)}>
      <div key={`${slideKey}-${active}`} style={visualStyle(layer, active)}>
        {inner}
      </div>
    </div>
  );

  if (layer.href) {
    return (
      <Link
        href={layer.href}
        target={layer.openInNewTab ? "_blank" : undefined}
        rel={layer.openInNewTab ? "noopener noreferrer" : undefined}
        style={{ display: "contents" }}
      >
        {node}
      </Link>
    );
  }
  return node;
}

export function SlideLayers({
  layers,
  active,
  currentLanguage = "en",
  slideKey,
  sectionNodes,
}: {
  layers?: HeroLayer[];
  active: boolean;
  currentLanguage?: "en" | "mm";
  /** Changes per active slide so entrance animations replay. */
  slideKey: number | string;
  /** Pre-rendered nodes for type 'section' layers, keyed by layer id. */
  sectionNodes?: Record<string, React.ReactNode>;
}) {
  if (!layers || layers.length === 0) return null;
  return (
    <>
      {layers.map((layer) => (
        <LayerNode
          key={layer.id}
          layer={layer}
          active={active}
          currentLanguage={currentLanguage}
          slideKey={slideKey}
          sectionNode={sectionNodes?.[layer.id]}
        />
      ))}
    </>
  );
}

export default SlideLayers;
